import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  GridLegacy as Grid,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Container,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Flag as FlagIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { challengeService } from '../../services/challengeService';
import { missionService } from '../../services/missionService';
import { teamService } from '../../services/teamService';
import { Challenge, Mission, Team } from '../../types';
import CreateChallengeDialog from './CreateChallengeDialog';
import CreateMissionDialog from './CreateMissionDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`challenges-tabpanel-${index}`}
      aria-labelledby={`challenges-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ChallengesMissionsPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [teamId, setTeamId] = useState<string>('');
  
  // Dialog states
  const [showCreateChallenge, setShowCreateChallenge] = useState(false);
  const [showCreateMission, setShowCreateMission] = useState(false);

  useEffect(() => {
    if (user && (user.role === 'coach' || user.role === 'headCoach')) {
      loadData();
    } else if (user && user.role === 'athlete') {
      // For athletes, don't load data - just show message
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Get user's teams (coaches can have multiple teams)
      // For coaches and head coaches, check both coach-owned teams and team memberships
      let teams: Team[] = [];
      if (user.role === 'coach' || user.role === 'headCoach') {
        // First ensure coach/head coach is added as member of their own teams
        await teamService.ensureCoachMembership(user.id);
        
        // Check both coach-owned teams and team memberships
        const [coachTeams, memberTeams] = await Promise.all([
          teamService.getTeamsByCoach(user.id),
          teamService.getTeamsByAthlete(user.id)
        ]);
        
        // Combine and deduplicate teams
        const allTeams = [...coachTeams];
        memberTeams.forEach(memberTeam => {
          if (!allTeams.find(team => team.id === memberTeam.id)) {
            allTeams.push(memberTeam);
          }
        });
        
        teams = allTeams;
      } else {
        teams = await teamService.getTeamsByAthlete(user.id);
      }
      
      if (teams.length === 0) {
        setError('You need to create a team first before creating challenges and missions.');
        setLoading(false);
        return;
      }

      // Load challenges and missions for the first team (for now)
      const firstTeamId = teams[0].id;
      setTeamId(firstTeamId);
      
      const [challengesData, missionsData] = await Promise.all([
        challengeService.getActiveChallenges(firstTeamId),
        missionService.getActiveMissions(firstTeamId)
      ]);
      
      setChallenges(challengesData);
      setMissions(missionsData);
    } catch (err: any) {
      console.error('Error loading challenges and missions:', err);
      setError(err.message || 'Failed to load challenges and missions');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreateChallenge = () => {
    setShowCreateChallenge(true);
  };

  const handleCreateMission = () => {
    setShowCreateMission(true);
  };

  const handleChallengeCreated = (challenge: Challenge) => {
    setChallenges(prev => [...prev, challenge]);
  };

  const handleMissionCreated = (mission: Mission) => {
    setMissions(prev => [...prev, mission]);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'No date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getChallengeTypeColor = (type: string) => {
    switch (type) {
      case 'daily': return 'success';
      case 'weekly': return 'warning';
      case 'monthly': return 'info';
      default: return 'default';
    }
  };

  const getMissionTypeColor = (type: string) => {
    switch (type) {
      case 'distance': return 'primary';
      case 'consistency': return 'success';
      case 'improvement': return 'warning';
      case 'team': return 'secondary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading challenges and missions...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (user?.role !== 'coach' && user?.role !== 'headCoach') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">
          Only coaches and head coaches can create and manage challenges and missions.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Challenges & Missions
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create and manage challenges and missions for your team members to earn bonus points.
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab 
            label={`Challenges (${challenges.length})`} 
            icon={<FlagIcon />}
            iconPosition="start"
          />
          <Tab 
            label={`Missions (${missions.length})`} 
            icon={<TrophyIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Active Challenges</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateChallenge}
          >
            Create Challenge
          </Button>
        </Box>

        {challenges.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <FlagIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Challenges Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first challenge to motivate your team members with bonus points!
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateChallenge}
              >
                Create Your First Challenge
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {challenges.map((challenge) => (
              <Grid item xs={12} md={6} key={challenge.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" component="div">
                        {challenge.title}
                      </Typography>
                      <Chip 
                        label={challenge.type} 
                        color={getChallengeTypeColor(challenge.type) as any}
                        size="small"
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {challenge.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrophyIcon color="warning" sx={{ fontSize: 20 }} />
                        <Typography variant="body2" fontWeight="bold">
                          {challenge.pointsReward} points
                        </Typography>
                      </Box>
                      <Chip 
                        label={challenge.isActive ? 'Active' : 'Inactive'} 
                        color={challenge.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      Created: {formatDate(challenge.createdAt)}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <IconButton size="small" color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Active Missions</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateMission}
          >
            Create Mission
          </Button>
        </Box>

        {missions.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <TrophyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Missions Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first mission to challenge your team members with bigger goals and rewards!
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateMission}
              >
                Create Your First Mission
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {missions.map((mission) => (
              <Grid item xs={12} md={6} key={mission.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" component="div">
                        {mission.title}
                      </Typography>
                      <Chip 
                        label={mission.type} 
                        color={getMissionTypeColor(mission.type) as any}
                        size="small"
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {mission.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrophyIcon color="warning" sx={{ fontSize: 20 }} />
                        <Typography variant="body2" fontWeight="bold">
                          {mission.pointsReward} points
                        </Typography>
                      </Box>
                      <Chip 
                        label={mission.isActive ? 'Active' : 'Inactive'} 
                        color={mission.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      Created: {formatDate(mission.createdAt)}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <IconButton size="small" color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Create Challenge Dialog */}
      {showCreateChallenge && teamId && (
        <CreateChallengeDialog
          open={showCreateChallenge}
          onClose={() => setShowCreateChallenge(false)}
          onChallengeCreated={handleChallengeCreated}
          teamId={teamId}
        />
      )}

      {/* Create Mission Dialog */}
      {showCreateMission && teamId && (
        <CreateMissionDialog
          open={showCreateMission}
          onClose={() => setShowCreateMission(false)}
          onMissionCreated={handleMissionCreated}
          teamId={teamId}
        />
      )}
    </Container>
  );
};

export default ChallengesMissionsPage;
